// src/domains/inventory/molecules/inventory-table-columns/inventory-table-columns.jsx

import SelectTableFilter from 'components/atoms/select-table-filter'
import StatusBadge from 'components/atoms/status-badge'
import ProductStatusBadge from 'domains/inventory/atoms/product-status-badge'
import { PRODUCT_STATUS } from 'domains/inventory/constants/inventory-constants'
import { normalizeInventoryPricePermissions } from 'domains/inventory/utils/inventory-price-permission-util'
import PropTypes from 'prop-types'
import React from 'react'
import { Button, Input } from 'reactstrap'
import PriceUtil from 'utils/price-util'

const propTypes = {
  buttonText       : PropTypes.string,
  buttonColour     : PropTypes.string,
  isButtonDisabled : PropTypes.func,
  onButtonClick    : PropTypes.func,
  pricePermissions : PropTypes.object,
  filterOptions    : PropTypes.object,
  extraButtons     : PropTypes.array
}

const filterPropTypes = {
  column: PropTypes.object
}

const PRODUCT_STATUS_FILTER_LABELS = {
  [PRODUCT_STATUS.ENABLED]  : 'Available',
  [PRODUCT_STATUS.DISABLED] : 'Not Available'
}

const PHOTO_LINK_EMAIL_SENT_FILTER_OPTIONS = [
  { label: 'Sent', value: 'true' },
  { label: 'Not Sent', value: 'false' }
]

const PRODUCT_CURATOR_BLANK_VALUE = '__blank__'

const PRODUCT_CURATOR_FILTER_OPTIONS = [
  { label: 'Kosong', value: PRODUCT_CURATOR_BLANK_VALUE },
  { label: 'MITA', value: 'Mita' },
  { label: 'DARA', value: 'Dara' },
  { label: 'SARAH', value: 'Sarah' },
  { label: 'AGNES', value: 'Agnes' },
  { label: 'VIKA', value: 'Vika' },
  { label: 'KARLA', value: 'Karla' },
  { label: 'JANE', value: 'Jane' },
  { label: 'LISA', value: 'Lisa' },
  { label: 'LUNA', value: 'Luna' },
  { label: 'ELLA', value: 'Ella' },
  { label: 'KARIN', value: 'Karin' },
  { label: 'Regina', value: 'REGINA' }
]

const normalizeValue = (value) => {
  if (value === undefined || value === null || value === '') return '-'

  return value
}

const getItemLocationLabel = (rowData = {}) => normalizeValue(
  rowData.item_location_name ||
  rowData.item_location_label ||
  rowData.location_item_name ||
  rowData.product_location_name ||
  rowData.product?.item_location_name ||
  rowData.product?.item_location_label ||
  rowData.product?.location_item_name ||
  rowData.product?.product_location_name ||
  rowData.product?.item_location?.name
)

const getBoxLocationLabel = (rowData = {}) => normalizeValue(
  rowData.box_location_name ||
  rowData.box_location_label ||
  rowData.box_location?.name ||
  rowData.product?.box_location_name ||
  rowData.product?.box_location?.name ||
  rowData.sub_location_label ||
  rowData.rack_label ||
  rowData.rack_name
)

const PriceCell = (row) => {
  const value = row.cell.value

  if (value === undefined || value === null || value === '') return '-'

  const normalizedValue = Number.isFinite(Number(value))
    ? Math.floor(Number(value)).toString()
    : String(value)

  return PriceUtil.format(normalizedValue)
}

const TextCell = (row) => normalizeValue(row.cell.value)

const ProductCuratorCell = (row) => {
  const value = normalizeValue(row.cell.value)

  if (value === '-') return '-'

  return value.toString().toUpperCase()
}

const InternalCategoryCell = (row) => {
  const value = normalizeValue(row.cell.value)

  if (value === '-') return '-'

  return value.toString().toUpperCase()
}

const SellerNameCell = (row) => {
  const rowData = row?.cell?.row?.original || {}
  const name = normalizeValue(row.cell.value || rowData.seller_name || rowData.consignor_seller_name)
  const sellerId = rowData.consignor_user_id || rowData.seller_id || rowData.consignor_id
  if (!sellerId || name === '-') return name

  return (
    <a
      href={ `/app/sales/users/${sellerId}` }
      className='font-weight-bold'
      target='_blank'
      rel='noopener noreferrer'
    >
      { name } #{ sellerId }
    </a>
  )
}

/**
 * Inventory Table Columns
 * @param {string} buttonText - The text that appears at the button on each row
 * @param {string} buttonColour - Bootstrap button type class name, example: primary
 * @param {Function} isButtonDisabled - function that will check whether the button on particular row is disabled
 * @param {Function} onButtonClick - function that will run when the button is clicked
 */
const InventoryTableColumns = ({
  onButtonClick,
  pricePermissions,
  filterOptions = {},
  extraButtons = []
}) => {
  const ProductStatusFilter = ({ column }) => {
    const options = Object.keys(PRODUCT_STATUS).map((key) => {
      const value = PRODUCT_STATUS[key]

      return {
        label : PRODUCT_STATUS_FILTER_LABELS[value] || value,
        value : value
      }
    })

    return (
      <SelectTableFilter reactTableColumn={ column } options={ options } />
    )
  }

  ProductStatusFilter.propTypes = filterPropTypes

  const ProductCuratorFilter = ({ column }) => (
    <SelectTableFilter reactTableColumn={ column } options={ PRODUCT_CURATOR_FILTER_OPTIONS } />
  )

  ProductCuratorFilter.propTypes = filterPropTypes

  const PhotoLinkEmailSentFilter = ({ column }) => (
    <SelectTableFilter reactTableColumn={ column } options={ PHOTO_LINK_EMAIL_SENT_FILTER_OPTIONS } />
  )

  PhotoLinkEmailSentFilter.propTypes = filterPropTypes

  const CategoryFilter = ({ column }) => (
    <SelectTableFilter
      reactTableColumn={ column }
      options={ filterOptions.categories || [] }
    />
  )

  CategoryFilter.propTypes = filterPropTypes

  const ConditionFilter = ({ column }) => (
    <SelectTableFilter
      reactTableColumn={ column }
      options={ filterOptions.conditions || [] }
    />
  )

  ConditionFilter.propTypes = filterPropTypes

  const ShoeSizeFilter = ({ column }) => (
    <SelectTableFilter
      reactTableColumn={ column }
      options={ filterOptions.sizes || [] }
    />
  )

  ShoeSizeFilter.propTypes = filterPropTypes

  const InternalCategoryFilter = ({ column }) => (
    <SelectTableFilter
      reactTableColumn={ column }
      options={ filterOptions.internal_categories || [] }
    />
  )

  InternalCategoryFilter.propTypes = filterPropTypes

  const ProductStatusCell = (row) => {
    const rowData = row?.cell?.row?.original || {}
    const markStatus = rowData.mark_sold_on_hold_status
    const returnStatus = rowData.consignor_return_status
    const statusBadgeProps = {
      className : 'badge-lg text-wrap d-inline-block',
      style     : { minWidth: '82px', textAlign: 'center' }
    }

    return (
      <div className='text-center'>
        <ProductStatusBadge product_status={ row.cell.value } { ...statusBadgeProps } />
        { markStatus === 'sold' && (
          <div className='mt-1'>
            <StatusBadge color='danger' label='SOLD' { ...statusBadgeProps } />
          </div>
        ) }
        { markStatus === 'on_hold' && (
          <div className='mt-1'>
            <StatusBadge color='warning' label='ON HOLD' { ...statusBadgeProps } />
          </div>
        ) }
        { markStatus !== 'sold' && returnStatus === 'returned' && (
          <div className='mt-1'>
            <StatusBadge color='info' label='RETURNED' { ...statusBadgeProps } />
          </div>
        ) }
      </div>
    )
  }

  const PhotoLinkSentCell = (row) => {
    const inventory = row.cell.row.original
    const sent = inventory.photo_link_email_sent === true
    const timestamp = inventory.photo_link_email_sent_at
    return (
      <div className='text-center'>
        <Input type='checkbox' checked={ sent } readOnly disabled className='position-static m-0' />
        { sent && timestamp && <small className='d-block text-muted mt-1'>{ new Date(timestamp).toLocaleString('id-ID') }</small> }
      </div>
    )
  }

  const SkuCell = (row) => {
    const inventory = row.cell.row.original
    const sku = inventory.product_sku || inventory.inventory_sku || '-'
    if (inventory.id) {
      return (
        <a
          href={ `/app/warehouse-team/inventory/${inventory.id}` }
          className='font-weight-bold'
          target='_blank'
          rel='noopener noreferrer'
        >
          { sku }
        </a>
      )
    }
    if (!onButtonClick) return sku

    return (
      <button
        type='button'
        className='btn btn-link p-0 font-weight-bold text-left'
        onClick={ () => onButtonClick(inventory) }
      >
        { sku }
      </button>
    )
  }

  const BatchCell = (row) => {
    const rowData = row?.cell?.row?.original || {}
    const batchCode = rowData.inventory_batch_code || rowData.consignment_inventory_batch_code || '-'
    if (!batchCode || batchCode === '-') return '-'

    return (
      <a
        className='font-weight-bold'
        href={ `/app/inbound-journey/inventory-batches?q=${encodeURIComponent(batchCode)}` }
        target='_blank'
        rel='noopener noreferrer'
      >
        { batchCode }
      </a>
    )
  }

  const ItemLocationCell = (row) => {
    const rowData = row?.cell?.row?.original || {}

    return getItemLocationLabel(rowData)
  }

  const LocationCell = (row) => {
    const rowData = row?.cell?.row?.original || {}

    return getBoxLocationLabel(rowData)
  }

  const ActionCell = (row) => {
    const rowData = row?.cell?.row?.original || {}
    const visibleButtons = extraButtons.filter((button) => !button.isVisible || button.isVisible(rowData))

    if (visibleButtons.length === 0) return '-'

    return (
      <div className='d-flex flex-nowrap' style={{ gap: 6 }}>
        { visibleButtons.map((button) => (
          <Button
            key={ button.key || button.text }
            type='button'
            color={ button.color || 'primary' }
            outline={ button.outline !== false }
            size='sm'
            title={ button.title || button.text }
            disabled={ button.isDisabled ? button.isDisabled(rowData) : false }
            onClick={ () => button.onClick && button.onClick(rowData) }
          >
            { button.icon ? <i className={ button.icon } /> : button.text }
          </Button>
        )) }
      </div>
    )
  }

  const {
    canViewBuyingPrice,
    canViewDefaultPrice,
    canViewSellingPrice,
    canViewSupplierName
  } = normalizeInventoryPricePermissions(pricePermissions)

  const priceColumns = [
    canViewBuyingPrice ? {
      Header         : 'Buying Price',
      accessor       : 'buying_price',
      disableFilters : true,
      Cell           : PriceCell
    } : null,
    canViewDefaultPrice ? {
      Header         : 'Default Price',
      accessor       : 'default_price',
      disableFilters : true,
      Cell           : PriceCell
    } : null,
    canViewSellingPrice ? {
      Header         : 'Selling Price',
      accessor       : 'selling_price',
      disableFilters : true,
      Cell           : PriceCell
    } : null
  ].filter(Boolean)

  return (
    [
      {
        Header         : 'SKU',
        accessor       : 'product_sku',
        disableFilters : true,
        Cell           : SkuCell
      },
      {
        Header   : 'Condition',
        id       : 'condition_id',
        accessor : 'condition_name',
        Filter   : ConditionFilter,
        Cell     : TextCell
      },
      {
        Header   : 'Category',
        id       : 'category_id',
        accessor : 'category_name',
        Filter   : CategoryFilter,
        Cell     : TextCell
      },
      {
        Header   : 'Shoe Size',
        id       : 'p_size',
        accessor : 'product_size',
        Filter   : ShoeSizeFilter,
        Cell     : TextCell
      },
      {
        Header   : <>Internal <br />Category</>,
        accessor : 'internal_category',
        Filter   : InternalCategoryFilter,
        Cell     : InternalCategoryCell
      },
      {
        Header         : 'Product Name',
        accessor       : 'product_name',
        disableFilters : true,
        Cell           : TextCell
      },
      canViewSupplierName ? {
        Header         : 'Consignor Name',
        accessor       : 'seller_name',
        disableFilters : true,
        Cell           : SellerNameCell
      } : null,
      {
        Header   : <>Product <br />Status</>,
        accessor : 'product_status',
        Filter   : ProductStatusFilter,
        Cell     : ProductStatusCell
      },
      {
        Header   : <>Product <br />Curator</>,
        accessor : 'product_curator_pic',
        Filter   : ProductCuratorFilter,
        Cell     : ProductCuratorCell
      },
      ...priceColumns,
      {
        Header         : 'Batch',
        accessor       : 'inventory_batch_code',
        disableFilters : true,
        Cell           : BatchCell
      },
      {
        Header         : 'SQ',
        accessor       : 'sellable_quantity',
        disableFilters : true
      },
      {
        Header         : 'IQ',
        accessor       : 'stored_quantity',
        disableFilters : true
      },
      {
        Header         : <>Item <br />Location</>,
        accessor       : 'item_location_name',
        disableFilters : true,
        Cell           : ItemLocationCell
      },
      {
        Header         : <>Sub-Location <br />/ Data Rak</>,
        accessor       : 'box_location_name',
        disableFilters : true,
        Cell           : LocationCell
      },
      {
        Header         : <>Photo Link<br />Sent</>,
        accessor       : 'photo_link_email_sent',
        Filter         : PhotoLinkEmailSentFilter,
        Cell           : PhotoLinkSentCell
      },
      extraButtons.length > 0 ? {
        Header         : 'Action',
        id             : 'inventory_actions',
        disableFilters : true,
        Cell           : ActionCell
      } : null
    ].filter(Boolean)
  )
}

InventoryTableColumns.propTypes = propTypes

export default InventoryTableColumns
